import React, { useCallback, useContext, useState } from 'react'
import { OffreModel } from '../types/OffreModel'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import styles from '../../../styles'
import { WHITE } from '../../../theme'
import PopinVisionneusePhotos from '../../../components/dialog/components/PopinVisionneusePhotos'
import PopinVisite3D from '../../../components/dialog/components/PopinVisite3D'
import { Swiper, SwiperSlide } from 'swiper/react'
import { makeStyles } from 'tss-react/mui'
import { Grid } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import PhotosBlocEnCommun from './PhotosBlocEnCommun'

const useStyles = makeStyles<{ isLargeScreen: boolean }>()((theme, { isLargeScreen }) => ({
	...styles(theme),
	photosContainer: {
		backgroundColor: WHITE,
		marginLeft: isLargeScreen ? 60 : 0,
		marginRight: isLargeScreen ? 60 : 0,
		marginTop: isLargeScreen ? 30 : 0
	},
	photoPrincipaleContainer: {
		padding: isLargeScreen ? '0 10px 0 0' : 0
	},
	photoPrincipale: {
		width: '100%',
		aspectRatio: isLargeScreen ? '710/400' : '428/320',
		backgroundSize: 'cover',
		position: 'relative',

		'&:hover': {
			'& .voirPlus': {
				display: 'inline-block'
			}
		}
	},
	photosDroiteBlocGauche: {
		padding: '0 10px',

		'& > div:first-child': {
			paddingBottom: 10
		},
		'& > div:last-child': {
			paddingTop: 10
		}
	},
	photosDroiteBlocDroite: {
		padding: '0 0 0 10px',

		'& > div:first-child': {
			paddingBottom: 10
		},
		'& > div:last-child': {
			paddingTop: 10
		}
	},
	photosSecondaires: {
		width: '100%',
		aspectRatio: '280/189',
		backgroundSize: 'cover',

		'& img': {
			objectFit: 'cover',
			width: '100%',
			height: '100%'
		}
	},
	voirPlusIcon: {
		width: 38,
		height: 38,
		padding: 27,
		borderRadius: 70,
		color: WHITE,
		background: '#F2AC97',
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		display: 'none',
		cursor: 'pointer',

		'&:hover': {
			background: theme.palette.primary.main
		}
	},
	partageContainer: {
		position: 'absolute',
		right: 30,
		top: 21,
		display: 'flex'
	},
	buttonStyle: {
		backgroundColor: '#eff3f5 !important',
		borderRadius: 25,
		padding: 0,
		width: 40,
		height: 40,
		minWidth: 40,
		minHeight: 40,
		marginLeft: 7
	},
	photosSwiper: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		objectPosition: 'center'
	},
	visionneusePlanStyle: {
		width: 1200
	}
}))

interface AcceptingProps {
	offre: OffreModel
}

type PhotosProps = AcceptingProps

const Photos: React.FC<PhotosProps> = (
	{
		offre
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const { classes, cx } = useStyles({ isLargeScreen })
	const [afficherVisionneusePhotos, setAfficherVisionneusePhotos] = useState<boolean>(false)
	const [afficherVisionneusePlan, setAfficherVisionneusePlan] = useState<boolean>(false)
	const [afficherVisite3D, setAfficherVisite3D] = useState<boolean>(false)

	// Fonction permettant de déterminer si la photo a été définie dans le CMS et de l'afficher si elle est présente
	const afficherPhoto = useCallback(
		(index: number) => {
			// S'il n'y a pas de photo pour l'index défini, on ne renvoie rien
			if (offre.photos.length < index) {
				return ''
			}

			// S'il y a un visuel de défini, on le renvoie
			if (offre.photos[index].visuel.length > 0) {
				return offre.photos[index].visuel
			}

			return ''
		}, [offre]
	)

	return (
		<Grid container item xs={12} className={classes.photosContainer}>
			{/* Bloc image de gauche, contenant les tags et le menu Media ainsi que la première image du CMS */}
			{
				isLargeScreen ? (
					<Grid
						container
						item
						xs={12}
						md={6}
						className={classes.photoPrincipaleContainer}
					>
						<Grid
							container
							item
							xs={12}
							className={classes.photoPrincipale}
						>
							<img
								src={afficherPhoto(0)}
								alt={afficherPhoto(0).split('/')[afficherPhoto(0).split('/').length - 1]}
								style={{
									objectFit: 'cover',
									width: '100%'
								}}
							/>

							<PhotosBlocEnCommun
								offre={offre}
								setAfficherVisionneusePhotos={setAfficherVisionneusePhotos}
								setAfficherVisionneusePlan={setAfficherVisionneusePlan}
								setAfficherVisite3D={setAfficherVisite3D}
							/>

							{/* Bloc "+" pour voir plus de photos */}
							<AddIcon
								className={
									cx(
										'voirPlus',
										classes.voirPlusIcon
									)
								}
								onClick={() => setAfficherVisionneusePhotos(true)}
							/>
						</Grid>
					</Grid>
				) : (
					<Swiper
						className={classes.photoPrincipale}
					>
						{
							offre.photos.map(
								(photo, index) => (
									<SwiperSlide
										key={`accueil-photos-${photo.visuel}-${index}`}
										onClick={() => setAfficherVisionneusePhotos(true)}
									>
										{/* On check si on est en mobile et qu'il existe une photo, sinon on utilise la photo large screen par défaut */}
										<img
											src={photo.visuel}
											alt={photo.alt}
											className={classes.photosSwiper}
										/>
										<PhotosBlocEnCommun
											offre={offre}
											setAfficherVisionneusePhotos={setAfficherVisionneusePhotos}
											setAfficherVisionneusePlan={setAfficherVisionneusePlan}
											setAfficherVisite3D={setAfficherVisite3D}
										/>
									</SwiperSlide>
								)
							)
						}
					</Swiper>
				)
			}

			{
				isLargeScreen && (
					<Grid container item xs={6}>
						{/* Bloc contenant la colonne de gauche */}
						<Grid container item xs={6} className={classes.photosDroiteBlocGauche}>
							{/* Bloc en haut à gauche */}
							<Grid container item xs={12}>
								<Grid
									item
									xs={12}
									className={classes.photosSecondaires}
								>
									<img
										src={afficherPhoto(1)}
										alt={afficherPhoto(1).split('/')[afficherPhoto(1).split('/').length - 1]}
									/>
								</Grid>
							</Grid>

							{/* Bloc en bas à gauche */}
							<Grid container item xs={12}>
								<Grid
									item
									xs={12}
									className={classes.photosSecondaires}
								>
									<img
										src={afficherPhoto(2)}
										alt={afficherPhoto(2).split('/')[afficherPhoto(2).split('/').length - 1]}
									/>
								</Grid>
							</Grid>
						</Grid>

						{/* Bloc contenant la colonne de droite */}
						<Grid container item xs={6} className={classes.photosDroiteBlocDroite}>
							{/* Bloc en haut à droite */}
							<Grid container item xs={12}>
								<Grid
									item
									xs={12}
									className={classes.photosSecondaires}
								>
									<img
										src={afficherPhoto(3)}
										alt={afficherPhoto(3).split('/')[afficherPhoto(3).split('/').length - 1]}
									/>
								</Grid>
							</Grid>

							{/* Bloc en bas à droite */}
							<Grid container item xs={12}>
								<Grid
									item
									xs={12}
									className={classes.photosSecondaires}
								>
									<img
										src={afficherPhoto(4)}
										alt={afficherPhoto(4).split('/')[afficherPhoto(4).split('/').length - 1]}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				)
			}

			<PopinVisionneusePhotos
				onCloseDialog={() => setAfficherVisionneusePhotos(false)}
				isOpen={afficherVisionneusePhotos}
				photos={offre.photos}
				swiperWidth={1200}
				swiperHeight={676}
			/>

			<PopinVisionneusePhotos
				onCloseDialog={() => setAfficherVisionneusePlan(false)}
				isOpen={afficherVisionneusePlan}
				photos={
					[
						{
							visuel: offre.plan || '',
							alt: ''
						}
					]
				}
				swiperContainerStyle={classes.visionneusePlanStyle}
				swiperWidth={1200}
				swiperHeight={676}
			/>

			<PopinVisite3D
				onCloseDialog={() => setAfficherVisite3D(false)}
				isOpen={afficherVisite3D}
				iframeURL={offre.visite3D || ''}
			/>
		</Grid>
	)
}

export default Photos